import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const ServiceDetailsPage = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const serviceDetails: Record<string, any> = {
    concrete: {
      title: 'Concrete Services',
      subtitle: 'Professional Concrete Solutions',
      description: 'At A and B Concrete LLC, we specialize in professional concrete services for residential and commercial projects. Our experienced team delivers high-quality concrete work that combines durability, functionality, and aesthetic appeal. From new installations to repairs, we handle every project with precision and care.',
      hero: 'https://public.readdy.ai/ai/img_res/e590221f9e1410a230beddc4da29c886.jpg',
      services: [
        {
          name: 'Concrete Driveways',
          description: 'Durable, attractive driveways designed to withstand heavy traffic and harsh weather conditions.',
          icon: 'ri-roadster-line',
        },
        {
          name: 'Foundation Work',
          description: 'Solid foundations that provide lasting support for your residential or commercial structure.',
          icon: 'ri-home-4-line',
        },
        {
          name: 'Concrete Slabs',
          description: 'Level, reinforced slabs perfect for garages, sheds, and outdoor structures.',
          icon: 'ri-layout-grid-line',
        },
        {
          name: 'Sidewalks & Walkways',
          description: 'Safe, smooth pathways that enhance accessibility and curb appeal.',
          icon: 'ri-footprint-line',
        },
        {
          name: 'Concrete Floors',
          description: 'Polished or finished concrete floors for basements, garages, and commercial spaces.',
          icon: 'ri-stack-line',
        },
        {
          name: 'Repairs & Resurfacing',
          description: 'Expert repair services to restore and extend the life of existing concrete.',
          icon: 'ri-tools-line',
        },
      ],
      process: [
        { step: '01', title: 'Consultation', description: 'We discuss your project needs and provide expert recommendations' },
        { step: '02', title: 'Site Preparation', description: 'Proper grading and preparation ensure a solid foundation' },
        { step: '03', title: 'Concrete Pouring', description: 'Professional installation using quality materials and techniques' },
        { step: '04', title: 'Finishing', description: 'Smooth finishing and curing for optimal strength and appearance' },
      ],
      benefits: [
        'Durable and long-lasting',
        'Low maintenance requirements',
        'Weather resistant',
        'Increases property value',
        'Versatile applications',
        'Cost-effective solution',
      ],
    },
    patio: {
      title: 'Patio Installation',
      subtitle: 'Transform Your Outdoor Living Space',
      description: 'Create the perfect outdoor entertainment area with our custom patio installation services. We design and build beautiful patios that extend your living space and provide a comfortable setting for gatherings, relaxation, and outdoor dining. Our team works with various materials and designs to match your home\'s style and your personal preferences.',
      hero: 'https://readdy.ai/api/search-image?query=Beautiful%20custom%20patio%20installation%20with%20decorative%20concrete%20patterns%20and%20elegant%20design%2C%20outdoor%20living%20space%20with%20furniture%20and%20plants%2C%20professional%20patio%20construction%2C%20simple%20neutral%20background%20highlighting%20the%20patio%20work%2C%20warm%20golden%20hour%20lighting%2C%20inviting%20outdoor%20entertainment%20area%2C%20modern%20residential%20backyard&width=1400&height=600&seq=detail-patio-hero&orientation=landscape',
      services: [
        {
          name: 'Custom Patio Design',
          description: 'Personalized designs that complement your home and lifestyle.',
          icon: 'ri-pencil-ruler-2-line',
        },
        {
          name: 'Stamped Concrete Patios',
          description: 'Decorative patterns that mimic stone, brick, or tile at a fraction of the cost.',
          icon: 'ri-palette-line',
        },
        {
          name: 'Colored Concrete',
          description: 'Wide range of colors to match your aesthetic preferences.',
          icon: 'ri-paint-brush-line',
        },
        {
          name: 'Outdoor Kitchen Areas',
          description: 'Integrated spaces for grilling and outdoor cooking.',
          icon: 'ri-restaurant-line',
        },
        {
          name: 'Fire Pit Installation',
          description: 'Cozy fire features for year-round outdoor enjoyment.',
          icon: 'ri-fire-line',
        },
        {
          name: 'Seating Walls',
          description: 'Built-in seating that maximizes space and functionality.',
          icon: 'ri-sofa-line',
        },
      ],
      process: [
        { step: '01', title: 'Design Consultation', description: 'Collaborate on layout, materials, and features' },
        { step: '02', title: 'Site Preparation', description: 'Excavation and base preparation for stability' },
        { step: '03', title: 'Installation', description: 'Professional construction with attention to detail' },
        { step: '04', title: 'Finishing Touches', description: 'Sealing and final details for lasting beauty' },
      ],
      benefits: [
        'Expands living space',
        'Increases home value',
        'Perfect for entertaining',
        'Low maintenance',
        'Customizable designs',
        'Durable construction',
      ],
    },
    pavers: {
      title: 'Paver Installation',
      subtitle: 'Elegant Hardscaping Solutions',
      description: 'Enhance your property with professional paver installation. Pavers offer unmatched versatility and beauty for driveways, walkways, patios, and more. Our expert team installs pavers with precision, creating stunning patterns and designs that elevate your outdoor spaces while providing durability and easy maintenance.',
      hero: 'https://readdy.ai/api/search-image?query=Professional%20paver%20installation%20with%20intricate%20geometric%20patterns%2C%20workers%20carefully%20placing%20interlocking%20pavers%20in%20beautiful%20design%2C%20driveway%20and%20walkway%20construction%2C%20detailed%20craftsmanship%2C%20simple%20neutral%20background%20highlighting%20the%20paver%20work%2C%20bright%20natural%20lighting%2C%20precise%20and%20clean%20installation%2C%20modern%20residential%20property&width=1400&height=600&seq=detail-pavers-hero&orientation=landscape',
      services: [
        {
          name: 'Paver Driveways',
          description: 'Stunning, durable driveways that make a lasting first impression.',
          icon: 'ri-car-line',
        },
        {
          name: 'Walkways & Paths',
          description: 'Beautiful pathways that guide visitors through your landscape.',
          icon: 'ri-walk-line',
        },
        {
          name: 'Paver Patios',
          description: 'Elegant outdoor living spaces with endless design possibilities.',
          icon: 'ri-home-smile-line',
        },
        {
          name: 'Pool Decks',
          description: 'Slip-resistant, attractive surfaces around your pool area.',
          icon: 'ri-water-flash-line',
        },
        {
          name: 'Retaining Walls',
          description: 'Functional and decorative walls for landscaping and erosion control.',
          icon: 'ri-building-line',
        },
        {
          name: 'Custom Patterns',
          description: 'Unique designs including herringbone, basket weave, and more.',
          icon: 'ri-grid-line',
        },
      ],
      process: [
        { step: '01', title: 'Design Selection', description: 'Choose from various paver styles, colors, and patterns' },
        { step: '02', title: 'Base Preparation', description: 'Proper excavation and base installation for stability' },
        { step: '03', title: 'Paver Installation', description: 'Precise placement and alignment of each paver' },
        { step: '04', title: 'Finishing', description: 'Joint sand application and sealing for longevity' },
      ],
      benefits: [
        'Wide variety of styles',
        'Easy to repair',
        'Excellent drainage',
        'Freeze-thaw resistant',
        'Increases curb appeal',
        'Long-lasting beauty',
      ],
    },
    drywall: {
      title: 'Drywall Services',
      subtitle: 'Professional Interior Finishing',
      description: 'Complete drywall services for new construction, renovations, and repairs. Our skilled craftsmen deliver smooth, flawless walls and ceilings ready for painting or finishing. We handle projects of all sizes, from single-room repairs to complete home installations, ensuring quality results every time.',
      hero: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20installation%20and%20finishing%20work%2C%20construction%20worker%20applying%20drywall%20compound%20and%20smoothing%20walls%20with%20tools%2C%20interior%20construction%20site%2C%20skilled%20craftsmanship%2C%20simple%20neutral%20background%20highlighting%20the%20drywall%20work%2C%20even%20lighting%2C%20clean%20professional%20workspace%2C%20modern%20residential%20interior&width=1400&height=600&seq=detail-drywall-hero&orientation=landscape',
      services: [
        {
          name: 'Drywall Installation',
          description: 'Complete installation for new construction and additions.',
          icon: 'ri-layout-masonry-line',
        },
        {
          name: 'Drywall Repair',
          description: 'Expert repairs for holes, cracks, and water damage.',
          icon: 'ri-hammer-line',
        },
        {
          name: 'Texturing',
          description: 'Various texture options including knockdown, orange peel, and smooth.',
          icon: 'ri-brush-line',
        },
        {
          name: 'Taping & Mudding',
          description: 'Professional finishing for seamless joints and surfaces.',
          icon: 'ri-paint-line',
        },
        {
          name: 'Ceiling Installation',
          description: 'Smooth or textured ceiling installation and repair.',
          icon: 'ri-home-8-line',
        },
        {
          name: 'Commercial Drywall',
          description: 'Large-scale projects for offices and commercial spaces.',
          icon: 'ri-building-2-line',
        },
      ],
      process: [
        { step: '01', title: 'Assessment', description: 'Evaluate the space and determine material needs' },
        { step: '02', title: 'Installation', description: 'Hang drywall sheets with precision and care' },
        { step: '03', title: 'Taping & Mudding', description: 'Apply joint compound and tape for smooth seams' },
        { step: '04', title: 'Finishing', description: 'Sand and prepare surfaces for painting' },
      ],
      benefits: [
        'Smooth, professional finish',
        'Fire-resistant materials',
        'Sound insulation',
        'Quick installation',
        'Cost-effective',
        'Ready for painting',
      ],
    },
  };

  const service = serviceDetails[serviceId || 'concrete'];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Service Not Found</h1>
          <Link to="/services" className="text-brand-green hover:underline cursor-pointer">
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.hero} alt={service.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/services" className="inline-flex items-center gap-2 text-brand-green hover:text-brand-green/80 mb-6 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back to Services</span>
            </Link>
            <h1 className="text-6xl lg:text-7xl font-black text-white mb-4">{service.title}</h1>
            <p className="text-2xl lg:text-3xl font-serif italic text-white/90">{service.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 bg-brand-light">
        <div className="max-w-4xl mx-auto px-6 lg:px-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xl text-gray-700 leading-relaxed"
          >
            {service.description}
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 lg:py-32" ref={ref}>
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-4">What We Offer</h2>
            <p className="text-xl text-gray-600">Comprehensive solutions for your project</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.services.map((item: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-brand-green/10 rounded-2xl mb-6">
                  <i className={`${item.icon} text-3xl text-brand-green`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.name}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-brand-gray">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black text-white mb-4">Our Process</h2>
            <p className="text-xl text-gray-300">How we bring your project to life</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-7xl font-black text-brand-green/20 mb-4">{step.step}</div>
                <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-xl text-gray-600">Why choose our services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.benefits.map((benefit: string, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-center gap-4 bg-brand-light rounded-xl p-6"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-brand-green text-2xl"></i>
                </div>
                <span className="text-lg font-semibold text-gray-800">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-green">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:5743776776"
              className="bg-white text-brand-gray px-10 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              (574) 377-6776
            </a>
            <a
              href="/#contact"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-semibold hover:bg-white hover:text-brand-green transition-all whitespace-nowrap cursor-pointer"
            >
              Request Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailsPage;